import { getCurrentDate, getCurrentDateMs } from '../CurrentDate';
import { actionCreators } from '../state';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { setTripCost } from '../state/action-creators';
import store from '../state/store';
import { parse, format, isValid as isValidDate } from 'date-fns';

type DataEval = {
  out?: string;
  readonly contract?: Contract;
  readonly environment?: Stage;
  readonly policyDeadlineType?: string;
  readonly tripBeginMaxDays?: number;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly geographicalZone?: GeographicalZoneType;
  readonly bookingConfirmationDate?: string;
  readonly residence?: string;
  readonly tripCost?: number;
  readonly excessAmount?: number;
  readonly rental?: boolean;
  readonly meansOfTravel?: MeansOfTravelType;
  readonly category?: string;
  readonly covidProtection?: boolean;
  readonly travelCancellation?: boolean;
  readonly excess?: boolean;
  productPlan?: ProductPlan;
  readonly productId?: number;
  readonly selectedProduct?: SinglePlan;
  readonly policyHolder?: {
    readonly salutation?: string | null;
    readonly firstName?: string;
    readonly lastName?: string;
    readonly company?: string;
    readonly samePerson?: boolean;
    readonly street?: string;
    readonly postalCode?: string;
    readonly city?: string;
    readonly country?: string;
    readonly phone?: string;
    readonly email?: string;
  };
  recordOfAdviceUrl?: string | null;
  preciItems?: Array<{ urlType: string }>;

  globalConfig?: {
    primary: {
      readonly defaultBackground: string;
      readonly activeBackground: string;
      readonly text: string;
    };
    secondary: {
      readonly promotion: string;
      readonly promotion2: string;
      readonly error: string;
    };
  };
  componentConfig?: {
    readonly primary?: {
      readonly color1?: string;
      readonly color2?: string;
      readonly color3?: string;
    };
    readonly secondary?: {
      readonly color1?: string;
      readonly color2?: string;
      readonly color3?: string;
    };
  };
  headerConfig?: {
    readonly background?: string;
    readonly text?: string;
  };
  navigationConfig?: {
    readonly background?: string;
    readonly activeBackground?: string;
    readonly text?: string;
  };
  persons?: Array<Person>;
  travelers?: TravelersType;
  paymentMethod: string;
  formatDate: Function;
  currentDate: Function;
  isDateRangeLargerThan: Function;
  isBookPolicyDedlineCorrect: Function;
  isDateInTheNextXDays: Function;
  hideEmail: Function;
  showErgoSpecificFields: boolean;
  ergoOrganisationUnitTypName: string | null;
  hotline: {
    phone: string;
    serviceTime: string;
  };
  promptagent: boolean;
  isMaklerPortalAgency: boolean;
  isMecklenburgische: boolean;
  isWuerttembergische: boolean;
  isMilesMore: boolean;
};

export default class Helper {
  static isAppNameCorrect = (appName: string) => {
    const AppList = [
      'ba-standard',
      'ba-standard2',
      'ba-tid',
      'ba-camping',
      'ba-schueler',
      'ba-bahn',
      'ba-langzeit',
      'ba-jv-std',
      'ba-jv-devk',
      'ba-incoming',
      'ba-cdw',
      'ba-wikinger',
      'ba-ticket',
      'ba-bus',
      'ba-jv-spez',
      'ba-jv-altmark',
    ];
    return AppList.includes(appName);
  };

  static appRedirects = (appName: string) => {
    if (appName === 'ba-standard2') return 'ba-standard';
    return appName;
  };

  static getReferrertUrl = () => {
    return window.location != window.parent.location ? document.referrer : document.location.href;
  };

  static getLabel(label: AppLabels | undefined) {
    if (!label) return '';
    let lang = store.getState().settingsReducer.language;
    return label[lang];
  }

  static getBackendDecimalAmount = (value: string) => {
    value = value.replace('.', '').replace(',', '.');
    return parseFloat(value);
  };

  static hideEmail = (email: string) => {
    const parts = email.split('@');
    if (parts.length === 2) {
      const [username, domain] = parts;
      const visibleUsername =
        username.length > 4
          ? username.substring(0, 2) +
            '*'.repeat(username.length - 4) +
            username.substring(username.length - 2)
          : username.substring(0, 2) + '*'.repeat(Math.max(username.length - 2, 0));
      return `${visibleUsername}@${domain}`;
    }
    return email;
  };

  static convertDaysToMilliSeconds = (days: number) => {
    return days * 24 * 60 * 60 * 1000;
  };

  static calculateMinBookingDays = (
    policyType: 'rrv_30_3_2023' | 'rrv_30_3',
    minBookingDaysParams?: number,
  ) => {
    let minBookingDays = minBookingDaysParams ? minBookingDaysParams : 4;
    if (policyType === 'rrv_30_3') {
      switch (getCurrentDate().getDay()) {
        case 0:
        case 1:
        case 2:
        case 3:
          return minBookingDays + 2;
        case 4:
        case 5:
        case 6:
          return minBookingDays;
        default:
          return minBookingDays;
      }
    } else {
      switch (getCurrentDate().getDay()) {
        case 1:
        case 2:
        case 3:
          return minBookingDays + 1;
        case 0:
        case 4:
        case 5:
        case 6:
          return minBookingDays;
        default:
          return minBookingDays;
      }
    }
  };

  static buildData = () => {
    const BookingState = store.getState().bookingReducer;
    const ProductState = store.getState().productsReducer;
    const SettingState = store.getState().settingsReducer;
    const ConfigState = store.getState().configReducer.properties;

    // TODO: find a better solution to distinguish diese Agencies ( EID-31585 ) and remove hard-coded agencies nummers
    let isMaklerPortalAgency = false;
    if (SettingState.partnerId === '089600000000' || SettingState.partnerId === '089800000000') {
      isMaklerPortalAgency = true;
    }

    let showErgoSpecificFields = false;
    if (
      SettingState.agencyInformation &&
      SettingState.agencyInformation.ergoOrganisationUnitType &&
      SettingState.agencyInformation.ergoOrganisationUnitType.name &&
      SettingState.agencyInformation.ergoOrganisationUnitType.organisation1Number.visible
    ) {
      showErgoSpecificFields = true;
    }

    let ergoOrganisationUnitTypName = '';
    if (
      SettingState.agencyInformation &&
      SettingState.agencyInformation.ergoOrganisationUnitType &&
      SettingState.agencyInformation.ergoOrganisationUnitType.name
    )
      ergoOrganisationUnitTypName = SettingState.agencyInformation.ergoOrganisationUnitType.name;

    let hotline = {
      phone: '+49 89 4166 - 1766',
      serviceTime: '(Mo.-Fr. 8-19 Uhr, Sa. 9-13 Uhr)',
    };
    if (
      SettingState.agencyInformation &&
      SettingState.agencyInformation.hotline &&
      SettingState.agencyInformation.hotline.phone
    )
      hotline.phone = SettingState.agencyInformation.hotline.phone;

    if (
      SettingState.agencyInformation &&
      SettingState.agencyInformation.hotline &&
      SettingState.agencyInformation.hotline.serviceTime
    )
      hotline.serviceTime = SettingState.agencyInformation.hotline.serviceTime;

    const data: DataEval = {
      out: '',
      contract: ProductState.contract,
      environment: SettingState.environment,
      promptagent: SettingState.promptagent,
      policyDeadlineType: ConfigState.policyDeadlineType,
      tripBeginMaxDays: ConfigState.tripBeginMaxDays,
      startDate: BookingState.startDate,
      endDate: BookingState.endDate,
      bookingConfirmationDate: BookingState.bookingConfirmationDate,
      geographicalZone: BookingState.geographicalZone,
      tripCost: BookingState.tripCost,
      excessAmount: BookingState.excessAmount,
      rental: BookingState.rental,
      residence: BookingState.residence,
      meansOfTravel: BookingState.meansOfTravel,
      category: BookingState.category,
      covidProtection: BookingState.covidProtection,
      travelCancellation: BookingState.travelCancellation,
      excess: BookingState.excess,
      productPlan: [],
      productId: ProductState.selectedProduct?.id,
      selectedProduct: ProductState.selectedProduct,
      policyHolder: {
        salutation: BookingState.policyHolderSalutation,
        firstName: BookingState.policyHolderFirstName,
        lastName: BookingState.policyHolderLastName,
        samePerson: BookingState.policyHolderSamePerson,
        company: BookingState.policyHolderCompany ? BookingState.policyHolderCompany : '',
        street: BookingState.policyHolderStreet,
        postalCode: BookingState.policyHolderPostalCode,
        city: BookingState.policyHolderCity,
        country: BookingState.policyHolderCountry,
        phone: BookingState.policyHolderPhone,
        email: BookingState.policyHolderEmail,
      },
      recordOfAdviceUrl: ProductState.recordOfAdviceUrl,
      preciItems: ProductState.preciItems,
      componentConfig: {},
      showErgoSpecificFields: showErgoSpecificFields,
      ergoOrganisationUnitTypName: ergoOrganisationUnitTypName,
      hotline: hotline,
      persons: [...BookingState.persons],
      travelers: BookingState.travelers,
      paymentMethod: BookingState.paymentMethod,
      formatDate: Helper.formatDate,
      currentDate: Helper.currentDate,
      isDateRangeLargerThan: Helper.isDateRangeLargerThan,
      isBookPolicyDedlineCorrect: Helper.isBookPolicyDedlineCorrect,
      isDateInTheNextXDays: Helper.isDateInTheNextXDays,
      hideEmail: Helper.hideEmail,
      isMaklerPortalAgency: isMaklerPortalAgency,
      isMecklenburgische: SettingState.agencyInformation?.isMecklenburgische
        ? SettingState.agencyInformation.isMecklenburgische
        : false,
      isWuerttembergische: SettingState.agencyInformation?.isWuerttembergische
        ? SettingState.agencyInformation.isWuerttembergische
        : false,
      isMilesMore: SettingState.agencyInformation?.isMilesMore
        ? SettingState.agencyInformation.isMilesMore
        : false,
    };
    if (ProductState.productPlan) data.productPlan = ProductState.productPlan;
    if (window.DEBUG) console.log(data);
    return data;
  };

  static buildDataWithColors = () => {
    const SkinState = store.getState().skinReducer;

    let data = Helper.buildData();
    data.globalConfig = {
      primary: {
        defaultBackground: SkinState.globalConfig.primary.defaultBackground,
        activeBackground: SkinState.globalConfig.primary.activeBackground,
        text: SkinState.globalConfig.primary.text,
      },
      secondary: {
        promotion: SkinState.globalConfig.secondary.promotion,
        promotion2: SkinState.globalConfig.secondary.promotion2,
        error: SkinState.globalConfig.secondary.error,
      },
    };
    data.componentConfig = {
      primary: {
        color1: SkinState.componentConfig.primary.color1,
        color2: SkinState.componentConfig.primary.color2,
        color3: SkinState.componentConfig.primary.color3,
      },
      secondary: {
        color1: SkinState.componentConfig.secondary.color1,
        color2: SkinState.componentConfig.secondary.color2,
        color3: SkinState.componentConfig.secondary.color3,
      },
    };
    data.headerConfig = {
      background: SkinState.headerConfig.background,
      text: SkinState.headerConfig.text,
    };
    data.navigationConfig = {
      background: SkinState.navigationConfig.background,
      activeBackground: SkinState.navigationConfig.activeBackground,
      text: SkinState.navigationConfig.text,
    };

    return data;
  };

  static currentDate = () => {
    return format(getCurrentDate(), 'dd.MM.yyyy');
  };

  static formatDate = (date?: string | undefined) => {
    if (!date) return '';
    let dateSubstrings = date.split('-');
    if (dateSubstrings.length === 3) {
      return dateSubstrings[2] + '.' + dateSubstrings[1] + '.' + dateSubstrings[0];
    } else return '';
  };

  static isDateInTheNextXDays = (x: number, date: string) => {
    return new Date(date + 'T23:59:59').getTime() - getCurrentDateMs() < x * 1000 * 60 * 60 * 24;
  };

  static tripDuration = (startDate: string, endDate: string) => {
    return Math.floor((Date.parse(endDate) - Date.parse(startDate)) / 86400000);
  };

  static isDateRangeLargerThan = (x: number, startDate: string, endDate: string) => {
    return (
      new Date(endDate + 'T23:59:59Z').getTime() - new Date(startDate + 'T00:00:00Z').getTime() >
      x * 1000 * 60 * 60 * 24
    );
  };

  static isBookPolicyDedlineCorrect = (
    policyDeadlineType: 'rrv_30_3_2023' | 'rrv_30_3',
    bookingConfirmationDate: string,
    startDate: string,
  ) => {
    let currentDateMS = getCurrentDateMs();
    let dedlineDateMS =
      currentDateMS - Helper.calculateMinBookingDays(policyDeadlineType, 4) * 1000 * 60 * 60 * 24;
    let bookingDateMs = new Date(bookingConfirmationDate + 'T00:00:00Z').getTime();
    if (!Helper.isDateInTheNextXDays(30, startDate)) {
      return true;
    } else if (bookingDateMs > dedlineDateMS) return true;
    return false;
  };

  static calculateAge = (date: Date, today: Date) => {
    var birthDate = new Date(date);
    var age = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  static getDaysBetweenDates = (d1: string | undefined, d2: string) => {
    // one day in milliseconds
    const oneDay = 1000 * 60 * 60 * 24;

    // skip if d2 has no value
    if (!d2) {
      return undefined;
    }

    let date1 = new Date();
    if (d1) {
      date1 = new Date(d1);
    }
    let date2 = new Date(d2);
    date1.setHours(0);
    date1.setMinutes(0);
    date1.setSeconds(0);
    date2.setHours(0);
    date2.setMinutes(0);
    date2.setSeconds(0);

    // Calculating the time difference between two dates
    let diffInTime = date2.getTime() - date1.getTime();
    // Calculating the no. of days between two dates
    const diffInDays = Math.round(diffInTime / oneDay);
    if (diffInDays === 0) {
      return 'zero';
    }
    return diffInDays.toString();
  };

  static evalInt = (value?: string, data?: any): number => {
    let val = Number.parseInt(Helper.evalString(value, data), 10);
    if (Number.isNaN(val)) throw 'evalInt: NaN';
    return val;
  };

  static evalIntOrNull = (value?: string, data?: any): number | null => {
    if (!value) return null;
    let val = Number.parseInt(Helper.evalString(value, data), 10);
    if (Number.isNaN(val)) return null;
    return val;
  };

  static evalString = (value?: string, data?: any): string => {
    if (value === undefined) {
      return '';
    }
    if (value.indexOf('(') === 0 && value.lastIndexOf(')') === value.length - 1) {
      try {
        value = 'return ' + value;
        if (window.DEBUG) console.log('evalString: ', value, data);
        let f = new Function('data', value);
        return f(data);
      } catch (e) {
        console.log('evalString error: ', e);
        throw new Error('evalString error: no string value');
      }
    } else return value;
  };

  static evalBoolean = (value: string, data?: any): boolean => {
    if (value.indexOf('(') === 0 && value.lastIndexOf(')') === value.length - 1) {
      try {
        value = 'return ' + value;
        if (window.DEBUG) console.log('evalBoolean: ', value, data);
        let f = new Function('data', value);
        return f(data);
      } catch (e) {
        console.log('evalBoolean error: ', e);
        throw new Error('evalBoolean error: no boolean value');
      }
    } else {
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
      throw new Error('evalBoolean error: no boolean value');
    }
  };

  static mergeProps = (fileProps: any, defaultPropsList: any, type: string = 'default'): any => {
    let props = { properties: {}, labels: {} };
    let componentProps = { properties: {}, labels: {} };
    if (defaultPropsList[type]) {
      componentProps.properties = {
        ...(defaultPropsList[type].properties ? defaultPropsList[type].properties : {}),
      };
      componentProps.labels = {
        ...(defaultPropsList[type].labels ? defaultPropsList[type].labels : {}),
      };
    }
    props.properties = {
      ...(componentProps.properties ? componentProps.properties : {}),
      ...(fileProps.properties ? fileProps.properties : {}),
    };
    props.labels = {
      ...(componentProps.labels ? componentProps.labels : {}),
      ...(fileProps.labels ? fileProps.labels : {}),
    };
    return props;
  };

  //TODO: fix this, use ProductNameLong from backend
  static fixTitle = (value: string): string => {
    if (value) {
      if (value.indexOf('oSB') != -1) {
        return value.replaceAll('oSB', 'ohne Selbstbeteiligung');
      }
      if (value.indexOf('CDW-SB-') != -1) {
        return value.replaceAll('CDW-SB-', 'CDW-Selbstbehalts-');
      }

      if (value.indexOf('SB') != -1) {
        return value.replaceAll('SB', 'Selbstbeteiligung');
      }
      if (value.indexOf('RAB') != -1) {
        return value.replaceAll('RAB', 'Reiseabbruch-Vers.');
      }
      if (value.indexOf(' f. ') != -1) {
        return value.replaceAll(' f. ', ' für ');
      }
    }
    return value;
  };

  static initPersons = (travelers: TravelersType, disableBirthdate: boolean) => {
    let tmp: Array<Person> = [];
    if (travelers.complementary)
      for (let i = 0; i < travelers.complementary; i++) {
        tmp.push({
          id: i,
          firstName: '',
          lastName: '',
          age: 54,
          ageType: 'complementary',
          index: i,
          individualTripCost: 0,
        });
        if (!disableBirthdate) {
          tmp[i].birthDate = '';
        }
      }
    if (travelers.adultsUpTo40) {
      for (let i = 0; i < travelers.adultsUpTo40; i++) {
        tmp.push({
          id: (travelers.complementary ? travelers.complementary : 0) + i,
          firstName: '',
          lastName: '',
          age: 30,
          ageType: 'adultsUpTo40',
          index: i,
          individualTripCost: 0,
        });
        if (!disableBirthdate) {
          tmp[tmp.length - 1].birthDate = '';
        }
      }
    }
    if (travelers.adultsUpTo64)
      for (let i = 0; i < travelers.adultsUpTo64; i++) {
        tmp.push({
          id:
            (travelers.complementary ? travelers.complementary : 0) +
            (travelers.adultsUpTo40 ? travelers.adultsUpTo40 : 0) +
            i,
          firstName: '',
          lastName: '',
          age: 45,
          ageType: 'adultsUpTo64',
          index: i,
          individualTripCost: 0,
        });
        if (!disableBirthdate) {
          tmp[i].birthDate = '';
        }
      }
    if (travelers.adultsFrom65)
      for (let i = 0; i < travelers.adultsFrom65; i++) {
        tmp.push({
          id:
            (travelers.complementary ? travelers.complementary : 0) +
            (travelers.adultsUpTo40 ? travelers.adultsUpTo40 : 0) +
            (travelers.adultsUpTo64 ? travelers.adultsUpTo64 : 0) +
            i,
          firstName: '',
          lastName: '',
          age: 70,
          ageType: 'adultsFrom65',
          index: i,
          individualTripCost: 0,
        });
        if (!disableBirthdate) {
          tmp[i].birthDate = '';
        }
      }
    if (travelers.children)
      for (let i = 0; i < travelers.children; i++) {
        tmp.push({
          id:
            (travelers.complementary ? travelers.complementary : 0) +
            (travelers.adultsUpTo40 ? travelers.adultsUpTo40 : 0) +
            (travelers.adultsUpTo64 ? travelers.adultsUpTo64 : 0) +
            (travelers.adultsFrom65 ? travelers.adultsFrom65 : 0) +
            i,
          firstName: '',
          lastName: '',
          age: 20,
          ageType: 'children',
          index: i,
          individualTripCost: 0,
        });
        if (!disableBirthdate) {
          tmp[i].birthDate = '';
        }
      }
    return tmp;
  };

  static builTravelersTypeByNumberPerson = (
    params: Record<string, any>,
    paramTravelers: TravelersType,
  ) => {
    if (params.get('number_persons') && !isNaN(+params.get('number_persons'))) {
      paramTravelers.adultsUpTo64 = +params.get('number_persons');
    }

    return paramTravelers;
  };
  static builTravelersTypeByTravelersParam = (
    params: Record<string, any>,
    paramTravelers: TravelersType,
  ) => {
    if (params.get('adultsupto40') && !isNaN(+params.get('adultsupto40'))) {
      paramTravelers.adultsUpTo40 = +params.get('adultsupto40');
    }
    if (params.get('adultsupto64') && !isNaN(+params.get('adultsupto64'))) {
      paramTravelers.adultsUpTo64 = +params.get('adultsupto64');
    } else if (params.get('adults') && !isNaN(+params.get('adults'))) {
      paramTravelers.adultsUpTo64 = +params.get('adults');
    }
    if (params.get('children') && !isNaN(+params.get('children'))) {
      paramTravelers.children = +params.get('children');
    }
    if (params.get('adultsfrom65') && !isNaN(+params.get('adultsfrom65'))) {
      paramTravelers.adultsFrom65 = +params.get('adultsfrom65');
    } else if (params.get('seniors') && !isNaN(+params.get('seniors'))) {
      paramTravelers.adultsFrom65 = +params.get('seniors');
    }
    return paramTravelers;
  };

  //TODO: unit test
  static setUICGIParams = (params: Record<string, any>, dispatch: Dispatch<AnyAction>) => {
    const {
      setCategory,
      setRental,
      setTravelers,
      setTripCost,
      setStartDate,
      setEndDate,
      setMeansOfTravel,
      setBookingConfirmationDate,
      setGeographicalZone,
      setPlaceOfResidence,
      setExcessAmount,
      setHideJV,
      setWayOfAccess,
    } = bindActionCreators(actionCreators, dispatch);

    let paramTravelers = {
      adultsUpTo40: 0,
      adultsUpTo64: 0,
      adultsFrom65: 0,
      children: 0,
      complementary: null,
    };
    const BookingState = store.getState().bookingReducer;

    Helper.builTravelersTypeByNumberPerson(params, paramTravelers);
    Helper.builTravelersTypeByTravelersParam(params, paramTravelers);

    setTravelers(paramTravelers);

    if (params.get('tripcost') && !isNaN(+params.get('tripcost'))) {
      setTripCost(+params.get('tripcost'));
    }

    let isStartDateSet = false;
    if (params.get('startdate')) {
      let startdate = parse(params.get('startdate'), 'dd.MM.yyyy', new Date());

      if (isValidDate(startdate)) {
        setStartDate(format(startdate, 'yyyy-MM-dd'));
        isStartDateSet = true;

        // if the enddate is null or invalid, we set a default value (startdate)
        setEndDate(format(startdate, 'yyyy-MM-dd'));
      }
    }
    if (params.get('enddate')) {
      let enddate = parse(params.get('enddate'), 'dd.MM.yyyy', new Date());

      if (isValidDate(enddate)) {
        setEndDate(format(enddate, 'yyyy-MM-dd'));
        if (!isStartDateSet) {
          // if the startdate is null or invalid, we set a default value (enddate)
          setStartDate(format(enddate, 'yyyy-MM-dd'));
        }
      }
    }
    if (params.get('tripbookingdate')) {
      let bookingConfirmationDate = parse(params.get('tripbookingdate'), 'dd.MM.yyyy', new Date());
      if (isValidDate(bookingConfirmationDate)) {
        if (bookingConfirmationDate.getTime() <= getCurrentDateMs()) {
          setBookingConfirmationDate(format(bookingConfirmationDate, 'yyyy-MM-dd'));
        }
      }
    }

    if (params.get('travel_region')) {
      if (params.get('travel_region') == 'europe') {
        setGeographicalZone('e');
      } else if (params.get('travel_region') == 'world') {
        setGeographicalZone('w');
      }
    }
    if (params.get('travel_type')) {
      if (params.get('travel_type') == 'car') {
        setMeansOfTravel('car');
      } else if (params.get('travel_type') == 'flight') {
        setMeansOfTravel('air');
      }
    }
    if (params.get('country_of_residence')) {
      let countries: Array<string> = ['DE', 'AT', 'BE', 'DK', 'FR', 'IT', 'LU', 'NL', 'PL', 'CZ'];
      let country_of_residence = params.get('country_of_residence').toUpperCase();
      if (countries.includes(country_of_residence)) {
        setPlaceOfResidence(country_of_residence);
      }
    }
    if (params.get('object')) {
      if (params.get('object') == 'yes') {
        setRental(true);
      } else if (params.get('object') == 'no') {
        setRental(false);
      }
    }
    if (params.get('vehicle')) {
      if (params.get('vehicle') == 'rent') {
        setCategory('caravaning');
      } else if (params.get('vehicle') == 'own') {
        setCategory('camping');
      }
    }
    if (params.get('insurance_type')) {
      if (params.get('insurance_type') == 'single') {
        setCategory('einmalversicherung');
      } else if (params.get('insurance_type') == 'annual') {
        setCategory('jahresversicherung');
      }
    }
    if (params.get('cdw_sum')) {
      let excessAmounts: Array<string> = ['4000', '6000', '8000'];
      let excessAmountParam = params.get('cdw_sum');
      if (excessAmounts.includes(excessAmountParam)) {
        setExcessAmount(+excessAmountParam);
      }
    }
    if (params.get('hidejv') !== null) {
      if (params.get('hidejv') === 'true' || params.get('hidejv') === '') {
        setHideJV(true);
      }
    }
    if (params.get('wayofaccess') && params.get('wayofaccess').length <= 4) {
      setWayOfAccess(params.get('wayofaccess'));
    }
  };
}
